import React, { useCallback, useEffect, useState } from 'react';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { ListItemsProps } from './ListItems.props';
import ListItemsMenu from '../ListItemsMenu';
import ListMenuSkeleton from '../ListMenuSkeleton';
import { classes, StyledList } from './ListItems.styles';

interface RouteParams {
  companySlugParam?: string;
  fundSlugParam?: string;
}

const ListItems = ({ menuItems, open, setOpenSidebar, setAnchorEl, closeDropDown, setSubMenuItem }: ListItemsProps) => {
  const [activeFirstLevel, setActiveFirstLevel] = useState<string | null>(null);
  const { companySlugParam, fundSlugParam } = useParams<RouteParams>();
  const navigate = useNavigate();
  const location = useLocation();

  const { date: dateQueryString } = queryString.parse(location.search);

  const handleMenu = (event: React.MouseEvent | null, menuItem: any) => {
    closeDropDown();
    if (menuItem.callback) {
      menuItem.callback();
      return;
    }
    if (event && menuItem.slug) {
      setActiveFirstLevel(activeFirstLevel === menuItem.slug ? null : menuItem.slug);
    }
  };

  const handleSubMenu = (event: React.MouseEvent | null, subMenuItem: any) => {
    setOpenSidebar(false);
    if (subMenuItem.callback) {
      subMenuItem.callback();
      return;
    }
    const dateParams = subMenuItem.measurementDateSlug ?? dateQueryString;

    navigate({ pathname: subMenuItem.url, search: `date=${dateParams}` });
  };

  const setExpandedMenuSection = useCallback(() => {
    menuItems.forEach(menuItem => {
      if (menuItem.subMenu && companySlugParam) {
        menuItem.subMenu.forEach(subMenuItem => {
          if (subMenuItem.slug === companySlugParam) {
            setActiveFirstLevel(menuItem.slug);
          }
        });
      }
    });
  }, [menuItems, companySlugParam]);

  useEffect(() => {
    if (!activeFirstLevel && menuItems) {
      setExpandedMenuSection();
    }
  }, [menuItems, activeFirstLevel, setExpandedMenuSection]);

  const openDropDown = (event: React.MouseEvent, menuItem: any, subMenuItem: any) => {
    // Open dropdown only if it's an actual company
    if (subMenuItem.subMenu) {
      setSubMenuItem({ ...subMenuItem, parent: menuItem });
      const target = event.target as HTMLElement;
      const listItem = target.tagName !== 'LI' ? target.closest('li') : target;
      if (listItem) {
        const element = listItem as HTMLElement;
        setAnchorEl(element.getBoundingClientRect(), element);
      }
    } else {
      // Close any dropdown when you hover over the Add Company button
      closeDropDown();
    }
  };

  return (
    <StyledList className={classes.root}>
      {menuItems.map(menuItem =>
        !menuItem.subMenu ? (
          <ListMenuSkeleton key={menuItem.slug} open={open ?? false} classes={classes} />
        ) : (
          <ListItemsMenu
            key={menuItem.slug}
            menuItem={menuItem}
            open={open ?? false}
            activeFirstLevel={activeFirstLevel ?? undefined}
            handleMenu={handleMenu}
            classes={classes}
            openDropDown={openDropDown}
            handleSubMenu={handleSubMenu}
            companySlugParam={companySlugParam}
            fundSlugParam={fundSlugParam}
          />
        )
      )}
    </StyledList>
  );
};

export default ListItems;
